import {
  ACESFilmicToneMapping, AdditiveBlending, BackSide, BoxGeometry, BufferAttribute, BufferGeometry, Color, DoubleSide,
  DirectionalLight, Euler, Group, HemisphereLight, Mesh, MeshBasicMaterial, PerspectiveCamera, PlaneGeometry, Points, RingGeometry, Scene,
  MeshStandardMaterial, PMREMGenerator, ShaderMaterial, SphereGeometry, SRGBColorSpace, TorusGeometry, Vector2, Vector3, WebGLRenderer, WebGLRenderTarget,
} from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { SeededRandom } from '../core/SeededRandom';
import { disposeObjectTree } from '../core/dispose';
import { qualityPixelRatio } from '../core/quality';
import type { ResolvedQuality } from '../types';
import { DESTINATIONS, MOONS, type Destination } from '../universe/catalog';
import type { Flight } from '../universe/Flight';
import * as shaders from './shaders';
import { Cockpit } from './Cockpit';
import { CockpitPass } from './CockpitPass';
import { SpaceDust } from './SpaceDust';
import { NebulaVolume, createCloudNoise } from './NebulaVolume';
import { makeRockField } from './RockField';
import { ASTEROIDS, ASTEROID_STREAMS, COMETS, SCENIC_BODIES, SCENIC_CLOUDS, STAR_CLUSTERS, STAR_STREAM, debrisPosition, regionPoint, streamPosition } from '../universe/scenery';
import { StellarField, deepStarPosition } from './StellarField';
import { ModelAssets } from './ModelAssets';
import { applyHullFinish } from './HullFinish';
import { STRUCTURES, type StructureKind } from '../universe/structures';
import stationUrl from '../assets/models/station.glb?url';
import wreckUrl from '../assets/models/wreck.glb?url';
import panelUrl from '../assets/models/panel.glb?url';
import modelColliders from '../assets/models/colliders.json';

export class UniverseScene {
  readonly renderer: WebGLRenderer;
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(55, 1, .08, 500000);
  readonly composer: EffectComposer;
  private bloom: UnrealBloomPass;
  private output: OutputPass;
  private renderPass: RenderPass;
  private cockpitScene = new Scene();
  private cockpitCamera = new PerspectiveCamera(55, 1, .08, 100);
  private cockpitPass: CockpitPass;
  private cockpitEnvironment: WebGLRenderTarget;
  private models = new ModelAssets();
  private structureBatches = new Map<StructureKind, Group>();
  private stellarFields: StellarField[] = [];
  private nebulae: NebulaVolume[] = [];
  private cloudNoise = createCloudNoise();
  private cockpit = new Cockpit();
  private depth = new SpaceDust();
  cockpitEnabled = true;
  private uniforms: ShaderMaterial[] = [];
  private planets: Mesh[] = [];
  private clouds: Mesh[] = [];
  private width = 1;
  private height = 1;
  private quality: ResolvedQuality;
  private disposed = false;
  time = 0;

  constructor(stage: HTMLElement, quality: ResolvedQuality) {
    this.quality = quality;
    this.renderer = new WebGLRenderer({ antialias: true, powerPreference: 'high-performance', alpha: false, preserveDrawingBuffer: true });
    this.renderer.outputColorSpace = SRGBColorSpace; this.renderer.toneMapping = ACESFilmicToneMapping; this.renderer.toneMappingExposure = 1.13;
    this.renderer.setClearColor(0x010105, 1); this.renderer.domElement.setAttribute('aria-label', '可自由飞行的连续三维宇宙'); stage.append(this.renderer.domElement);
    this.renderer.info.autoReset = false;
    const environment = new RoomEnvironment(), pmrem = new PMREMGenerator(this.renderer);
    this.cockpitEnvironment = pmrem.fromScene(environment, .04); this.cockpitScene.environment = this.cockpitEnvironment.texture;
    this.cockpitScene.environmentIntensity = .36;
    environment.dispose(); pmrem.dispose();
    this.composer = new EffectComposer(this.renderer); this.renderPass = new RenderPass(this.scene, this.camera);
    this.bloom = new UnrealBloomPass(new Vector2(1, 1), .48, .65, .85); this.output = new OutputPass();
    this.cockpitPass = new CockpitPass(this.cockpitScene, this.cockpitCamera, (texture, width, height) => this.cockpit.setExterior(texture, width, height));
    this.composer.addPass(this.renderPass); this.composer.addPass(this.cockpitPass); this.composer.addPass(this.bloom); this.composer.addPass(this.output);
    const deepStars = this.makeStellarField(22000, 52, deepStarPosition, 1.8, 60000);
    deepStars.root.name = 'Fixed deep-space stars'; this.scene.add(deepStars.root);
    this.scene.add(this.depth.root, this.camera); this.cockpitScene.add(this.cockpit.root);
    const sunlight = new DirectionalLight('#ffe1b6', 3.2); sunlight.position.set(-800, 450, 700);
    this.scene.add(sunlight, new HemisphereLight('#87a4cf', '#15212d', 1.5));
    DESTINATIONS.forEach(item => this.makeDestination(item));
    this.makeLandscape();
    for (const moon of MOONS) this.makePlanet(moon.position, moon.radius, 3);
    const sun = new Mesh(new SphereGeometry(18,48,32),new MeshBasicMaterial({color:new Color(6,3.8,1.8)}));sun.position.set(-800,450,700);this.scene.add(sun);
    const glow = new Mesh(new PlaneGeometry(290,290),new ShaderMaterial({vertexShader:shaders.cloudVertex,fragmentShader:shaders.glowFragment,uniforms:{uColor:{value:new Color(1.8,.85,.3)}},transparent:true,blending:AdditiveBlending,depthWrite:false,side:DoubleSide}));glow.position.copy(sun.position);this.clouds.push(glow);this.scene.add(glow);
    // A physical debris field around Aurelia, visible while flying through its rings.
    const dust = this.makeStars(2200, 22, random => {
      const a = random.range(0, Math.PI * 2), r = random.range(48, 76);
      return new Vector3(Math.cos(a) * r, Math.sin(a) * r, random.signed(.15)).applyEuler(new Euler(1.17, .12, -.32));
    }, true);
    (dust.material as ShaderMaterial).uniforms.uScale.value = .13; this.scene.add(dust);
    this.setQuality(quality);
    for (const [kind, url] of Object.entries({ station: stationUrl, wreck: wreckUrl, panel: panelUrl })) {
      // Match collision silhouettes while the authored model downloads, also on failure.
      const pieces = modelColliders[kind as StructureKind].map(box => {
        const geometry = new BoxGeometry(...box.size as [number, number, number]);
        geometry.rotateZ(box.rotation); geometry.translate(...box.center as [number, number, number]); return geometry;
      });
      const placeholder = new Group();
      placeholder.add(new Mesh(mergeGeometries(pieces)!, new MeshStandardMaterial({ color: '#687e87', roughness: .55, metalness: .3 })));
      pieces.forEach(piece => piece.dispose()); this.installScenery(kind as StructureKind, placeholder);
      void this.models.load(kind, url, root => this.installScenery(kind as StructureKind, root));
    }
  }

  private installScenery(kind: StructureKind, model: Group) {
    if (kind === 'wreck') applyHullFinish(model, true);
    const previous = this.structureBatches.get(kind);
    if (previous) { this.scene.remove(previous); disposeObjectTree(previous); }
    const batch = new Group(); this.structureBatches.set(kind, batch); this.scene.add(batch);
    for (const item of STRUCTURES.filter(item => item.kind === kind)) {
      const placement = new Group(); placement.name = `Unmarked ${kind}`;
      placement.position.set(...item.position); placement.rotation.set(...item.rotation); placement.scale.setScalar(item.scale);
      placement.add(model.clone(true)); batch.add(placement);
    }
  }

  private makeStellarField(count: number, seed: number, point: (random: SeededRandom, i: number) => Vector3, radiusScale = 1, visibility = 1600) {
    const field = new StellarField(count, seed, point, radiusScale, visibility); this.stellarFields.push(field); return field;
  }

  private makeStars(count: number, seed: number, point: (random: SeededRandom, i: number) => Vector3, attenuate: boolean) {
    const random = new SeededRandom(seed), positions = new Float32Array(count * 3), colors = new Float32Array(count * 3), sizes = new Float32Array(count);
    const palette = [new Color('#d4e2ff'), new Color('#f7e9d0'), new Color('#82b5ed'), new Color('#e5b79a'), new Color('#ffffff')];
    for (let i = 0; i < count; i++) {
      point(random, i).toArray(positions, i * 3);
      const c = palette[random.int(0, palette.length - 1)].clone().multiplyScalar(random.range(.4, 1.75)); c.toArray(colors, i * 3);
      sizes[i] = random.next() > .993 ? random.range(5, 10) : random.range(.65, 2.8);
    }
    const geometry = new BufferGeometry(); geometry.setAttribute('position', new BufferAttribute(positions, 3)); geometry.setAttribute('aColor', new BufferAttribute(colors, 3)); geometry.setAttribute('aSize', new BufferAttribute(sizes, 1));
    const material = new ShaderMaterial({ vertexShader: shaders.starVertex, fragmentShader: shaders.starFragment, uniforms: { uRatio: { value: 1 }, uScale: { value: 1 }, uAttenuate: { value: attenuate ? 1 : 0 } }, blending: AdditiveBlending, depthWrite: false, transparent: true });
    this.uniforms.push(material); const stars = new Points(geometry, material); stars.frustumCulled = false; return stars;
  }
  private makePlanet(position: [number, number, number], radius: number, type: number, detail = 112) {
    const geometry = new SphereGeometry(radius, detail, Math.round(detail * .64));
    const material = new ShaderMaterial({ vertexShader: shaders.planetVertex, fragmentShader: shaders.planetFragment, uniforms: { uType: { value: type }, uTime: { value: 0 } } });
    const planet = new Mesh(geometry, material); planet.position.set(...position); this.planets.push(planet); this.uniforms.push(material); this.scene.add(planet);
    if (type < 3 || type === 4 || type === 6) {
      const atmosphere = new Mesh(new SphereGeometry(radius * 1.045, Math.min(80, detail), Math.min(56, detail)), new ShaderMaterial({ vertexShader: shaders.planetVertex, fragmentShader: shaders.atmosphereFragment, uniforms: { uColor: { value: new Color(type === 0 ? '#dfad6a' : type === 1 || type >= 4 ? '#459dff' : '#d94217') } }, side: BackSide, transparent: true, blending: AdditiveBlending, depthWrite: false }));
      atmosphere.position.copy(planet.position); this.scene.add(atmosphere);
    }
    return planet;
  }
  private ring(item: Pick<Destination, 'position' | 'radius'>, inner: number, outer: number, black = false) {
    const material = new ShaderMaterial({ vertexShader: shaders.ringVertex, fragmentShader: shaders.ringFragment, side: DoubleSide, transparent: true, depthWrite: false,
      uniforms: { uInner: { value: inner }, uOuter: { value: outer }, uRadius: { value: item.radius }, uCenter: { value: new Vector3(...item.position) }, uBlack: { value: black ? 1 : 0 }, uTime: { value: 0 } } });
    this.uniforms.push(material);
    const ring = new Mesh(new RingGeometry(inner, outer, 256, 1), material); ring.position.set(...item.position); ring.rotation.set(black ? 1.33 : 1.17, .12, -.32); this.scene.add(ring);
  }
  private makeDestination(item: Destination) {
    if (item.kind === 'ringed') { this.makePlanet(item.position, item.radius, 0); this.ring(item, 42, 76); }
    if (item.kind === 'ocean') this.makePlanet(item.position, item.radius, 1);
    if (item.kind === 'lava') this.makePlanet(item.position, item.radius, 2);
    if (item.kind === 'blackhole') {
      const hole = new Mesh(new SphereGeometry(item.radius, 96, 64), new MeshBasicMaterial({ color: 0x000000 })); hole.position.set(...item.position); this.scene.add(hole);
      this.ring(item, 34, 116, true);
      // Art-directed photon ring; all elements remain physical objects in this shared space.
      const photon = new Mesh(new TorusGeometry(33.5, .65, 10, 192), new MeshBasicMaterial({ color: new Color(3, 1.4, .45) })); photon.position.copy(hole.position); this.scene.add(photon);
    }
    if (item.kind === 'nebula') {
      const volume = new NebulaVolume(item, this.cloudNoise); this.nebulae.push(volume); this.scene.add(volume.mesh);
      const [x, y, z] = item.nebula!.extent;
      const stars = this.makeStellarField(1800, Math.round(item.nebula!.seed * 100), random => new Vector3(random.signed(x * .8), random.signed(y * .8), random.signed(z * .8)), .35);
      stars.root.position.set(...item.position); this.scene.add(stars.root);
    }
    if (item.kind === 'galaxy') {
      const galaxy = new Group(); galaxy.position.set(...item.position); galaxy.rotation.z = -.22;
      const haze = new NebulaVolume(item, this.cloudNoise); this.nebulae.push(haze); this.scene.add(haze.mesh);
      const field = this.makeStellarField(26000, 182, random => {
        const radius = Math.pow(random.next(), .68) * 850, arm = random.int(0, 3) * Math.PI / 2;
        const a = arm + radius * .008 + random.signed(.34) * (1 + radius / 800);
        return new Vector3(Math.cos(a) * radius, random.signed(12) * (1 - radius / 1000), Math.sin(a) * radius);
      }, .12);
      const stars = field.points;
      const positions=stars.geometry.getAttribute('position'),colors=stars.geometry.getAttribute('aColor');
      const inner=new Color('#f8c7a0'),outer=new Color('#94b8ee');
      for(let i=0;i<positions.count;i++){const radius=Math.hypot(positions.getX(i),positions.getZ(i))/850;const color=inner.clone().lerp(outer,Math.min(1,radius*1.7));colors.setXYZ(i,color.r*1.35,color.g*1.35,color.b*1.35);}
      galaxy.add(field.root); this.scene.add(galaxy);
      const core = this.makeStellarField(3500, 901, random => {
        const r = random.range(0, 115), a = random.range(0, Math.PI * 2), p = random.range(-1, 1);
        return new Vector3(Math.cos(a) * r, p * r * .45, Math.sin(a) * r);
      }, .08); galaxy.add(core.root);
    }
  }
  private makeLandscape() {
    for (const region of SCENIC_CLOUDS) {
      const volume = new NebulaVolume(region, this.cloudNoise); this.nebulae.push(volume); this.scene.add(volume.mesh);
    }
    const ribbon = this.makeStellarField(58000, 846, random => regionPoint(STAR_STREAM, streamPosition(random)), .8, 22000);
    ribbon.root.name = 'Unmarked stellar river'; ribbon.points.material.uniforms.uIntensity.value = .68;
    this.scene.add(ribbon.root);
    for (const cluster of STAR_CLUSTERS) {
      const field = this.makeStellarField(cluster.count, cluster.seed, random => {
        const radius = Math.pow(random.next(), 1.7) * cluster.radius, angle = random.range(0, Math.PI * 2), y = random.signed();
        return new Vector3(Math.cos(angle) * Math.sqrt(1 - y * y), y, Math.sin(angle) * Math.sqrt(1 - y * y)).multiplyScalar(radius);
      }, .4, 6500);
      field.root.position.set(...cluster.position); field.points.material.uniforms.uIntensity.value = .75; this.scene.add(field.root);
    }
    for (const body of SCENIC_BODIES) {
      this.makePlanet(body.position, body.radius, body.type, 64);
      if (body.rings) this.ring(body, body.radius * 1.38, body.radius * 2.15);
    }
    this.scene.add(makeRockField());
    for (const [index, stream] of ASTEROID_STREAMS.entries()) {
      const grains = this.makeStars(1100, 631 + index, random => debrisPosition(random, stream), true);
      (grains.material as ShaderMaterial).uniforms.uScale.value = .18; this.scene.add(grains);
    }
    // Two compact icy nuclei and long, fixed, fanning dust tails, with no map pins.
    for (const [index, comet] of COMETS.entries()) {
      const position = comet.position;
      this.makePlanet(position, comet.radius, comet.type, 32);
      const tail = this.makeStars(2200, 745 + index, random => {
        const t = random.next(), spread = t * 27;
        return new Vector3(position[0] + t * 300 + random.signed(spread), position[1] - t * 80 + random.signed(spread), position[2] + t * 640 + random.signed(spread));
      }, true);
      const colors = tail.geometry.getAttribute('aColor');
      for (let i = 0; i < colors.count; i++) colors.setXYZ(i, .18, .37, .55);
      (tail.material as ShaderMaterial).uniforms.uScale.value = 1.7; this.scene.add(tail);
    }
  }
  setQuality(quality: ResolvedQuality) {
    this.quality = quality; this.renderer.setPixelRatio(qualityPixelRatio(quality)); this.composer.setPixelRatio(qualityPixelRatio(quality));
    // The composer renders offscreen, so canvas antialias alone never smooths
    // windscreen edges. Reallocate its own buffers when the quality level changes.
    const samples = Math.min(this.renderer.capabilities.maxSamples, quality === 'high' ? 4 : quality === 'medium' ? 2 : 0);
    for (const target of [this.composer.renderTarget1, this.composer.renderTarget2]) {
      if (target.samples !== samples) { target.dispose(); target.samples = samples; }
    }
    this.bloom.enabled = quality !== 'low';
    for (const field of this.stellarFields) field.setQuality(quality);
    for (const nebula of this.nebulae) nebula.setQuality(quality);
    for (const mat of this.uniforms) if (mat.uniforms.uRatio) mat.uniforms.uRatio.value = qualityPixelRatio(quality);
    this.depth.setQuality(quality, qualityPixelRatio(quality));
    this.resize(this.width, this.height);
  }
  resize(width: number, height: number) {
    this.width = Math.max(1, width); this.height = Math.max(1, height);
    this.camera.aspect = this.width / this.height; this.camera.fov = width < 700 && height > width ? 82 : 55; this.camera.updateProjectionMatrix();
    this.cockpitCamera.aspect = this.camera.aspect; this.cockpitCamera.fov = this.camera.fov; this.cockpitCamera.updateProjectionMatrix();
    this.cockpit.setViewport(this.width, this.height, this.camera.fov);
    this.renderer.setSize(this.width, this.height); this.composer.setSize(this.width, this.height);
  }
  render(flight: Flight, dt: number, reduced: boolean, photograph = false) {
    if (this.disposed) return;
    this.time += reduced ? 0 : dt;
    this.camera.position.copy(flight.position); this.camera.quaternion.copy(flight.rotation); this.camera.updateMatrixWorld();
    this.depth.root.visible = !photograph; this.depth.update(flight.position, flight.velocity, reduced);
    this.cockpit.root.visible = this.cockpitEnabled && !photograph; this.cockpit.update(flight, dt, reduced);
    this.cockpitPass.enabled = this.cockpit.root.visible;
    for (const material of this.uniforms) if (material.uniforms.uTime) material.uniforms.uTime.value = this.time;
    if (!reduced) for (const planet of this.planets) planet.rotation.y += dt * .009;
    for (const field of this.stellarFields) field.update(this.camera.position, this.height, this.camera.fov, this.renderer.getPixelRatio());
    for (const nebula of this.nebulae) nebula.update(this.camera.position);
    for (const cloud of this.clouds) cloud.quaternion.copy(this.camera.quaternion);
    this.renderer.info.reset(); this.composer.render(dt);
  }
  project(point: Vector3) { return point.clone().project(this.camera); }
  get stats() { return { geometries: this.renderer.info.memory.geometries, textures: this.renderer.info.memory.textures, calls: this.renderer.info.render.calls, quality: this.quality, disposed: this.disposed,
    unmarkedBodies: SCENIC_BODIES.length, fixedAsteroids: ASTEROIDS.length, unmarkedClouds: SCENIC_CLOUDS.length,
    fixedStars: this.stellarFields.reduce((sum, field) => sum + field.stats.fixedStars, 0), nearbyStarCores: this.stellarFields.reduce((sum, field) => sum + field.stats.nearbyStarCores, 0),
    volumetricNebulae: this.nebulae.filter(nebula => nebula.destination.kind === 'nebula').length, volumetricGalaxies: this.nebulae.filter(nebula => nebula.destination.kind === 'galaxy').length,
    insideNebulae: this.nebulae.filter(nebula => nebula.inside).map(nebula => nebula.destination.id),
    unmarkedStructures: STRUCTURES.length, ...this.models.stats, ...this.cockpit.stats, ...this.depth.stats }; }
  async photograph() {
    return new Promise<Blob | null>(resolve => this.renderer.domElement.toBlob(resolve, 'image/png'));
  }
  dispose() {
    if (this.disposed) return; this.disposed = true;
    this.models.dispose();
    this.cockpitEnvironment.dispose();
    this.cockpit.releaseWindshieldInputs();
    this.cockpitPass.dispose();
    disposeObjectTree(this.scene); disposeObjectTree(this.cockpitScene); this.scene.clear(); this.cockpitScene.clear();
    this.bloom.dispose(); this.output.dispose(); this.renderPass.dispose(); this.composer.dispose();
    this.renderer.renderLists.dispose(); this.renderer.dispose(); this.renderer.domElement.remove();
  }
}
